import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { MapContainer, TileLayer, Marker, Popup, Polygon, Tooltip, useMap } from 'react-leaflet';
import MarkerClusterGroup from 'react-leaflet-cluster';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import iconRetinaUrl from 'leaflet/dist/images/marker-icon-2x.png';
import iconUrl from 'leaflet/dist/images/marker-icon.png';
import shadowUrl from 'leaflet/dist/images/marker-shadow.png';
import { api } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { ASSET_ICONS, ASSET_LABELS, ASSET_TYPES, formatDateTime, timeAgo } from '../utils';
import geofences from '../data/geofences.json';

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({ iconRetinaUrl, iconUrl, shadowUrl });

const DEFAULT_CENTER = [34.05, -118.15];
const DEFAULT_ZOOM = 9;

// Site boundaries exported from Samsara — real polygons, not radius circles.
// Regenerate with `npm run geofences -- <place list csv>`; the file is
// pre-sorted largest-first so nested sites (a tip floor inside a campus) paint
// on top and stay clickable.
//
// Emerald against the default blue device pins, with a light fill so markers
// sitting inside a boundary stay readable.
const SITE_STYLE = {
  color: '#059669', weight: 2, opacity: 0.9,
  fillColor: '#059669', fillOpacity: 0.08,
};

// Every vertex of every site. Leaflet accepts a flat array of points as bounds.
const SITE_POINTS = geofences.flatMap(g => g.polygon);

// Selected site gets a heavier stroke and a warmer fill so it stands out from
// the other 72 once the list zooms to it.
const SITE_STYLE_SELECTED = {
  color: '#b45309', weight: 3, opacity: 1,
  fillColor: '#f59e0b', fillOpacity: 0.2,
};

// Re-fits the map whenever `focus` becomes a new object — used both by "fit to
// sites" and by clicking a row in the site list. Keeping it in one component
// means only one place touches the map imperatively. fitBounds needs the map
// instance, which is only reachable from a child of MapContainer.
//
// `focus` must come from state, never a fresh object built during render, or the
// effect would re-fire on every render and fight the user's panning.
function MapFocus({ focus }) {
  const map = useMap();
  useEffect(() => {
    if (focus?.points?.length) {
      map.fitBounds(focus.points, { padding: [40, 40], maxZoom: focus.maxZoom ?? 18 });
    }
  }, [focus, map]);
  return null;
}

// Staleness filter — each option keeps only devices whose last Hexnode report
// is OLDER than the given age, for finding devices that have stopped checking in.
const AGE_FILTERS = [
  { value: '',    label: 'Any time' },
  { value: '1h',  label: 'Over 1 hour ago',   hours: 1 },
  { value: '1d',  label: 'Over 1 day ago',    hours: 24 },
  { value: '7d',  label: 'Over 1 week ago',   hours: 24 * 7 },
  { value: '30d', label: 'Over 30 days ago',  hours: 24 * 30 },
  { value: '90d', label: 'Over 90 days ago',  hours: 24 * 90 },
];

// Hexnode reports coordinates as strings, and devices that have never checked in
// come back with null/empty ones — those would become NaN and make Leaflet throw.
function toLatLng(device) {
  const lat = parseFloat(device.latitude);
  const lng = parseFloat(device.longitude);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  if (lat < -90 || lat > 90 || lng < -180 || lng > 180) return null;
  return [lat, lng];
}

export default function MapPage() {
  const { withAuth } = useAuth();
  const navigate = useNavigate();
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filterType, setFilterType] = useState('');
  const [filterAge, setFilterAge] = useState('');
  const [search, setSearch] = useState('');
  const [showSites, setShowSites] = useState(true);
  const [showSiteList, setShowSiteList] = useState(true);
  const [siteSearch, setSiteSearch] = useState('');
  const [selectedSiteId, setSelectedSiteId] = useState(null);
  const [athensOnly, setAthensOnly] = useState(true);
  // A new object each time is what re-triggers the fit, so repeat clicks on the
  // same site still re-zoom after the user has panned away.
  const [focus, setFocus] = useState(null);

  // Most Samsara places are third-party disposal sites and customer stops, not
  // Athens facilities. `workday` is set by the build script for places that
  // matched a Workday location, so it doubles as the "this is ours" flag.
  //
  // Kept in the source order (largest polygon first) — nested sites like a tip
  // floor inside a campus have to paint last to stay clickable.
  const mapSites = athensOnly ? geofences.filter(s => s.workday) : geofences;
  const athensCount = geofences.filter(s => s.workday).length;

  // The list is sorted by name, which is a different order from the map's.
  const visibleSites = mapSites
    .filter(s => {
      if (!siteSearch) return true;
      const q = siteSearch.toLowerCase();
      // Samsara's own name is searchable too — it's what someone cross-checking
      // against Samsara itself would type, and it's how a campus is told apart
      // from its tip floor once both display the same Workday name.
      return s.name.toLowerCase().includes(q)
        || (s.samsaraName || '').toLowerCase().includes(q)
        || (s.address || '').toLowerCase().includes(q)
        || (s.workday?.ref || '').toLowerCase().includes(q);
    })
    .slice()
    .sort((a, b) => a.name.localeCompare(b.name) || (a.samsaraName || '').localeCompare(b.samsaraName || ''));

  function focusSite(site) {
    setSelectedSiteId(site.id);
    setFocus({ points: site.polygon, maxZoom: 17 });
    // Zooming to a boundary the user has hidden would land on empty tiles.
    setShowSites(true);
  }

  function fitAllSites() {
    setSelectedSiteId(null);
    const points = mapSites.flatMap(s => s.polygon);
    setFocus({ points: points.length ? points : SITE_POINTS });
  }

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      await withAuth(async () => {
        const res = await api.getAssetMap();
        setDevices(res.devices || []);
      });
    } catch (err) {
      console.error(err);
      setError(err.message || 'Could not load device locations.');
      setDevices([]);
    } finally {
      setLoading(false);
    }
  }, [withAuth]);

  useEffect(() => { load(); }, [load]);

  // Devices without usable coordinates can't be drawn — count them so the
  // totals in the header don't silently disagree with what's on the map.
  const located = devices
    .map(d => ({ ...d, position: toLatLng(d) }))
    .filter(d => d.position);
  const missingLocation = devices.length - located.length;

  // Evaluated once per render so every device is compared against the same instant.
  const now = Date.now();
  const ageCutoffHours = AGE_FILTERS.find(f => f.value === filterAge)?.hours;

  const filtered = located.filter(d => {
    if (filterType && d.asset_type !== filterType) return false;
    if (ageCutoffHours) {
      const reported = d.reported_at ? new Date(d.reported_at).getTime() : NaN;
      // A device with no usable timestamp has no known recent check-in, so it
      // belongs in every "older than" bucket rather than being filtered away.
      if (Number.isFinite(reported) && now - reported < ageCutoffHours * 3600000) return false;
    }
    if (search) {
      const q = search.toLowerCase();
      const hit = (d.display_name || '').toLowerCase().includes(q)
        || (d.asset_tag || '').toLowerCase().includes(q)
        || (d.employee_name || '').toLowerCase().includes(q)
        || (d.serial_number || '').toLowerCase().includes(q);
      if (!hit) return false;
    }
    return true;
  });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
      <div style={{ padding: '20px 28px', borderBottom: '2px solid var(--accent)', background: 'var(--bg-1)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div>
          <h1 style={{ fontSize: 18, fontWeight: 600 }}>Device Map</h1>
          <p style={{ color: 'var(--text-2)', fontSize: 13, marginTop: 2 }}>
            {loading ? 'Loading…' : `${filtered.length.toLocaleString()} of ${located.length.toLocaleString()} devices`} — locations from Hexnode
            {!loading && missingLocation > 0 && (
              <> · {missingLocation.toLocaleString()} without a reported location</>
            )}
          </p>
        </div>
        <button className="btn btn-sm" onClick={load} disabled={loading}>
          {loading ? 'Refreshing…' : 'Refresh'}
        </button>
      </div>

      <div style={{ padding: '14px 28px', borderBottom: '1px solid var(--border)', display: 'flex', gap: 10, flexWrap: 'wrap', background: 'var(--bg-1)' }}>
        <input
          placeholder="Search device, employee, or serial…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{ width: 300 }}
        />
        <select value={filterType} onChange={e => setFilterType(e.target.value)} style={{ width: 160 }}>
          <option value="">All Types</option>
          {ASSET_TYPES.map(t => (
            <option key={t} value={t}>{ASSET_LABELS[t]}</option>
          ))}
        </select>
        <select value={filterAge} onChange={e => setFilterAge(e.target.value)} style={{ width: 205 }}>
          {AGE_FILTERS.map(f => (
            <option key={f.value} value={f.value}>
              {f.value ? `Reported: ${f.label}` : 'Reported: Any time'}
            </option>
          ))}
        </select>
        {(search || filterType || filterAge) && (
          <button className="btn btn-sm" onClick={() => { setSearch(''); setFilterType(''); setFilterAge(''); }}>Clear</button>
        )}

        <div style={{ marginLeft: 'auto', display: 'flex', gap: 10, alignItems: 'center' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 14, color: 'var(--text-1)', cursor: 'pointer' }}>
            <input type="checkbox" checked={showSites} onChange={e => setShowSites(e.target.checked)} />
            <span style={{
              width: 10, height: 10, borderRadius: 2, flexShrink: 0,
              background: 'rgba(5,150,105,0.25)', border: `1px solid ${SITE_STYLE.color}`,
            }} />
            Site boundaries ({mapSites.length})
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 14, color: 'var(--text-1)', cursor: 'pointer' }}
                 title={`${athensCount} of ${geofences.length} Samsara places matched a Workday location. The rest are disposal sites and customer stops.`}>
            <input type="checkbox" checked={athensOnly} onChange={e => setAthensOnly(e.target.checked)} />
            Athens sites only
          </label>
          <button className="btn btn-sm" onClick={() => setShowSiteList(v => !v)}>
            {showSiteList ? '◀ Hide sites' : '▶ Site list'}
          </button>
          <button className="btn btn-sm" onClick={fitAllSites} title="Zoom out to show every site">
            Fit to sites
          </button>
        </div>
      </div>

      <div style={{ flex: 1, minHeight: 0, display: 'flex' }}>
        {showSiteList && geofences.length > 0 && (
          <aside style={{
            width: 260, flexShrink: 0, display: 'flex', flexDirection: 'column',
            borderRight: '1px solid var(--border)', background: 'var(--bg-1)',
          }}>
            <div style={{ padding: '10px 12px', borderBottom: '1px solid var(--border)' }}>
              <input
                placeholder="Find a site…"
                value={siteSearch}
                onChange={e => setSiteSearch(e.target.value)}
                style={{ width: '100%' }}
              />
              <div style={{ fontSize: 12, color: 'var(--text-2)', marginTop: 6 }}>
                {visibleSites.length === mapSites.length
                  ? `${mapSites.length} sites`
                  : `${visibleSites.length} of ${mapSites.length} sites`}
              </div>
            </div>

            <div style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
              {visibleSites.length === 0 ? (
                <div style={{ padding: '16px 12px', fontSize: 13, color: 'var(--text-2)' }}>
                  No sites match “{siteSearch}”.
                </div>
              ) : visibleSites.map(site => {
                const active = site.id === selectedSiteId;
                return (
                  <button
                    key={site.id}
                    onClick={() => focusSite(site)}
                    title={site.address || site.name}
                    style={{
                      display: 'block', width: '100%', textAlign: 'left',
                      padding: '8px 12px', border: 'none', cursor: 'pointer',
                      background: active ? 'var(--bg-3)' : 'transparent',
                      borderLeft: `2px solid ${active ? SITE_STYLE_SELECTED.color : 'transparent'}`,
                      color: 'var(--text-0)', fontFamily: 'inherit',
                    }}
                  >
                    <div style={{ fontSize: 13, fontWeight: active ? 600 : 500 }}>
                      {site.name}
                      {site.workday?.review && (
                        <span title={site.workday.basis} style={{ color: 'var(--yellow)', marginLeft: 5 }}>⚠</span>
                      )}
                    </div>
                    {/* Two places can share one Workday location — a campus and
                        its tip floor both read "Crown MRF" — so the Samsara
                        name is what distinguishes the rows. */}
                    {(site.samsaraName !== site.name || site.workday) && (
                      <div style={{ fontSize: 12, color: 'var(--text-2)', marginTop: 1 }}>
                        {site.samsaraName !== site.name && site.samsaraName}
                        {site.samsaraName !== site.name && site.workday && ' · '}
                        {site.workday && <span className="mono">{site.workday.ref}</span>}
                      </div>
                    )}
                    {site.address && (
                      <div style={{
                        fontSize: 12, color: 'var(--text-2)', marginTop: 1,
                        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                      }}>
                        {site.address}
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          </aside>
        )}

        <div style={{ flex: 1, minHeight: 0 }}>
        {loading ? (
          <div className="empty-state"><div className="spinner" /></div>
        ) : error ? (
          <div className="empty-state">
            <div style={{ fontSize: 24, marginBottom: 10 }}>⚠</div>
            <div style={{ color: 'var(--red)', marginBottom: 4 }}>Could not load device locations</div>
            <div style={{ marginBottom: 16 }}>{error}</div>
            <button className="btn btn-sm" onClick={load}>Try again</button>
          </div>
        ) : located.length === 0 && geofences.length === 0 ? (
          <div className="empty-state">
            <div style={{ fontSize: 24, marginBottom: 10 }}>🗺️</div>
            <div>
              {devices.length === 0
                ? 'No devices are reporting locations yet.'
                : `None of the ${devices.length.toLocaleString()} devices have a reported location.`}
            </div>
          </div>
        ) : (
          <MapContainer center={DEFAULT_CENTER} zoom={DEFAULT_ZOOM} style={{ height: '100%', width: '100%' }}>
            <TileLayer
              attribution='&copy; <a href="https://openstreetmap.org">OpenStreetMap</a>'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            <MapFocus focus={focus} />

            {/* Before the markers so device pins stay on top of the fills. */}
            {showSites && mapSites.map(site => (
              <Polygon
                key={site.id}
                positions={site.polygon}
                pathOptions={site.id === selectedSiteId ? SITE_STYLE_SELECTED : SITE_STYLE}
                eventHandlers={{ click: () => focusSite(site) }}
              >
                <Tooltip sticky>
                  <div style={{ fontWeight: 600, fontSize: 13 }}>{site.name}</div>
                  {site.samsaraName !== site.name && (
                    <div style={{ fontSize: 12, color: '#888' }}>{site.samsaraName} in Samsara</div>
                  )}
                  {site.address && (
                    <div style={{ fontSize: 12, color: '#666', maxWidth: 240 }}>{site.address}</div>
                  )}
                </Tooltip>
              </Polygon>
            ))}

            <MarkerClusterGroup chunkedLoading maxClusterRadius={50}>
              {filtered.map(d => (
                <Marker key={d.asset_id} position={d.position}>
                  <Popup>
                    <div style={{ minWidth: 220 }}>
                      <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 4 }}>
                        {ASSET_ICONS[d.asset_type]} {d.display_name}
                      </div>
                      <div style={{ fontSize: 12, color: '#666', marginBottom: 8 }}>
                        {ASSET_LABELS[d.asset_type]} · {d.asset_tag}
                      </div>
                      {d.employee_name && (
                        <div style={{ fontSize: 13, marginBottom: 4 }}>
                          <strong>Assigned:</strong> {d.employee_name}
                        </div>
                      )}
                      {d.address && (
                        <div style={{ fontSize: 13, marginBottom: 4 }}>
                          <strong>Location:</strong> {d.address}
                        </div>
                      )}
                      <div style={{ fontSize: 12, color: '#888', marginBottom: 8 }}>
                        <strong style={{ color: '#666' }}>Last reported:</strong>{' '}
                        {d.reported_at ? (
                          <>
                            {formatDateTime(d.reported_at)}
                            <span style={{ color: '#999' }}> ({timeAgo(d.reported_at)})</span>
                          </>
                        ) : 'Never'}
                      </div>
                      <button
                        className="btn btn-sm btn-primary"
                        style={{ width: '100%' }}
                        onClick={() => navigate(`/assets/${d.asset_id}`)}
                      >
                        View asset details →
                      </button>
                    </div>
                  </Popup>
                </Marker>
              ))}
            </MarkerClusterGroup>
          </MapContainer>
        )}
        </div>
      </div>
    </div>
  );
}
